/**
 * OneHand layer - Single hand game mode.
 *
 * Handles phase transition and selective action automation for one-hand mode:
 * - Optional demo intro: scripted bidding/trump when `scriptedOneHandIntro` is set
 * - Auto-executes score-hand after consensus for smooth UX
 * - Phase transition: prevents new hand after scoring (returns 'one-hand-complete')
 */

import type { Layer } from './types';
import type { GameState, GamePhase, GameAction } from '../types';

function scriptedIntroActions(state: GameState): GameAction[] | null {
  if (!state.initialConfig.scriptedOneHandIntro) {
    return null;
  }

  if (state.phase === 'bidding') {
    const currentPlayer = state.currentPlayer;

    switch (state.bids.length) {
      case 0:
        return [{
          type: 'pass' as const,
          player: currentPlayer,
          autoExecute: true,
          meta: { scriptId: 'one-hand-setup', step: 1, authority: 'system' as const }
        }];
      case 1:
        return [{
          type: 'pass' as const,
          player: currentPlayer,
          autoExecute: true,
          meta: { scriptId: 'one-hand-setup', step: 2, authority: 'system' as const }
        }];
      case 2:
        return [{
          type: 'pass' as const,
          player: currentPlayer,
          autoExecute: true,
          meta: { scriptId: 'one-hand-setup', step: 3, authority: 'system' as const }
        }];
      case 3:
        return [{
          type: 'bid' as const,
          player: currentPlayer,
          bid: 'points' as const,
          value: 30,
          autoExecute: true,
          meta: { scriptId: 'one-hand-setup', step: 4, authority: 'system' as const }
        }];
      default:
        return null;
    }
  }

  if (state.phase === 'trump_selection') {
    return [
      {
        type: 'select-trump' as const,
        player: state.winningBidder,
        trump: { type: 'suit' as const, suit: 4 },
        autoExecute: true,
        meta: { scriptId: 'one-hand-setup', step: 5, authority: 'system' as const }
      }
    ];
  }

  return null;
}

export const oneHandLayer: Layer = {
  name: 'oneHand',

  /**
   * Demo intro scripting (opt-in) and score-hand auto-execute.
   */
  getValidActions: (state: GameState, prev: GameAction[]): GameAction[] => {
    const intro = scriptedIntroActions(state);
    if (intro) {
      return intro;
    }

    const hasScoreHand = prev.some(a => a.type === 'score-hand');
    if (state.phase === 'scoring' && hasScoreHand) {
      return [
        ...prev.filter(a => a.type !== 'score-hand'),
        {
          type: 'score-hand' as const,
          autoExecute: true,
          meta: { scriptId: 'one-hand-scoring', authority: 'system' as const }
        }
      ];
    }

    return prev;
  },

  rules: {
    /**
     * After hand is scored, transition to 'one-hand-complete' instead of 'bidding'.
     */
    getPhaseAfterHandComplete: (_state: GameState, _prev: GamePhase): GamePhase => {
      return 'one-hand-complete';
    }
  }
};
